import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';

/**
 * 駐車場の答えの下に出す地図。正本は gardens（coin_parkings・address・photo_parking_url）。
 * 地図は Google マップの埋め込み（鍵なし）。複数のパーキングはタブで切り替え、
 * 「地図アプリで開く」「園までの歩く道順」でスマホの地図アプリへ渡す。
 */
type Cp = { name: string; address?: string; note?: string; walk_min?: number; lat?: number; lng?: number };
type G = { name: string; address: string | null; has_own_parking: boolean | null; photo_parking_url: string | null; coin_parkings: Cp[] | null; show_coin_parking: boolean | null };

const where = (c: Cp) => (c.lat != null && c.lng != null ? `${c.lat},${c.lng}` : (c.address ?? c.name));

export default function ParkingMap({ garden, accent }: { garden: string; accent: string }) {
  const [g, setG] = useState<G | null>(null);
  const [i, setI] = useState(0);
  useEffect(() => {
    supabase.from('gardens').select('name,address,has_own_parking,photo_parking_url,coin_parkings,show_coin_parking').eq('slug', garden).maybeSingle()
      .then(({ data }) => { if (data) setG(data as G); });
  }, [garden]);
  if (!g) return null;
  const cps = (g.show_coin_parking ? g.coin_parkings : null) ?? [];
  const gardenWhere = g.address ?? g.name;

  return (
    <div className="mt-3 space-y-3">
      {g.has_own_parking && g.photo_parking_url && (
        <figure className="rounded-2xl overflow-hidden border border-gray-200">
          <img src={g.photo_parking_url} alt="園の駐車場" className="w-full h-auto block" loading="lazy" />
          <figcaption className="text-sm text-gray-600 px-3 py-2 bg-white">園の駐車場</figcaption>
        </figure>
      )}
      {cps.length > 0 && (
        <div className="rounded-2xl border border-gray-200 overflow-hidden bg-white">
          {cps.length > 1 && (
            <div className="flex gap-1 p-1 bg-gray-50 overflow-x-auto">
              {cps.map((c, n) => (
                <button key={c.name} onClick={() => setI(n)}
                  className={`flex-shrink-0 px-3 py-2 rounded-xl text-sm cursor-pointer ${n === i ? 'bg-white font-bold shadow-sm' : 'text-gray-600'}`}
                  style={n === i ? { color: accent } : undefined}>
                  {c.name.length > 12 ? `${c.name.slice(0, 12)}…` : c.name}
                </button>
              ))}
            </div>
          )}
          {(() => {
            const c = cps[Math.min(i, cps.length - 1)];
            return (
              <div>
                <iframe
                  title={c.name}
                  src={`https://maps.google.com/maps?q=${encodeURIComponent(where(c))}&z=17&output=embed`}
                  className="w-full block border-0"
                  style={{ height: 220 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
                <div className="p-3">
                  <p className="text-base font-bold text-gray-800">{c.name}</p>
                  <p className="text-sm text-gray-600">
                    {typeof c.walk_min === 'number' ? `園まで徒歩約${c.walk_min}分` : ''}{c.note ? `・${c.note}` : ''}
                  </p>
                  {c.address && <p className="text-sm text-gray-500 mt-0.5">{c.address}</p>}
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(where(c))}`} target="_blank" rel="noopener noreferrer"
                      className="text-center rounded-xl py-3 text-sm font-bold text-white" style={{ background: accent }}>地図アプリで開く</a>
                    <a href={`https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(where(c))}&destination=${encodeURIComponent(gardenWhere)}&travelmode=walking`} target="_blank" rel="noopener noreferrer"
                      className="text-center rounded-xl py-3 text-sm font-bold bg-white border-2 border-gray-200 text-gray-700">園までの歩く道順</a>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}
